import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Calendar, Clock, MapPin, User, ChevronLeft, ChevronRight, X } from 'lucide-react';

const Termine = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewTerminModal, setShowNewTerminModal] = useState(false);
  const [newTermin, setNewTermin] = useState({
    titel: '',
    kunde: '',
    fahrzeug: '',
    datum: '',
    uhrzeit: '',
    ort: '',
    notizen: ''
  });

  const [termine, setTermine] = useState([
    {
      id: 1,
      titel: 'Fahrzeugbesichtigung',
      kunde: 'Max Müller',
      fahrzeug: 'BMW 320d',
      datum: new Date(2025, 10, 20),
      uhrzeit: '10:00',
      ort: 'Werkstatt Berlin Mitte',
      status: 'Bestätigt',
      color: 'bg-blue-500'
    },
    {
      id: 2,
      titel: 'Schadensbegutachtung',
      kunde: 'Anna Schmidt',
      fahrzeug: 'Mercedes C-Klasse',
      datum: new Date(2025, 10, 21),
      uhrzeit: '14:30',
      ort: 'München Zentrum',
      status: 'Bestätigt',
      color: 'bg-green-500'
    },
    {
      id: 3,
      titel: 'Wertgutachten',
      kunde: 'Peter Wagner',
      fahrzeug: 'Audi A4',
      datum: new Date(2025, 10, 22),
      uhrzeit: '09:00',
      ort: 'Hamburg Hafen',
      status: 'Ausstehend',
      color: 'bg-yellow-500'
    },
    {
      id: 4,
      titel: 'Gutachten Erstellen',
      kunde: 'Lisa Bauer',
      fahrzeug: 'VW Golf',
      datum: new Date(2025, 10, 15),
      uhrzeit: '11:00',
      ort: 'Berlin',
      status: 'Bestätigt',
      color: 'bg-purple-500'
    }
  ]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getTermineForDay = (day) => {
    return termine.filter(termin => {
      const terminDate = termin.datum;
      return terminDate.getDate() === day &&
             terminDate.getMonth() === currentDate.getMonth() &&
             terminDate.getFullYear() === currentDate.getFullYear();
    });
  };

  const handleCreateTermin = () => {
    if (!newTermin.titel || !newTermin.datum || !newTermin.uhrzeit) {
      alert('Bitte füllen Sie alle Pflichtfelder aus!');
      return;
    }

    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    const neuerTermin = {
      id: termine.length + 1,
      ...newTermin,
      datum: new Date(newTermin.datum),
      status: 'Bestätigt',
      color: randomColor
    };

    setTermine([...termine, neuerTermin]);
    setShowNewTerminModal(false);
    setNewTermin({
      titel: '',
      kunde: '',
      fahrzeug: '',
      datum: '',
      uhrzeit: '',
      ort: '',
      notizen: ''
    });
  };

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const today = new Date();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Terminkalender</h1>
            <p className="text-gray-500 mt-1">Verwalten Sie Ihre Termine und Besichtigungen</p>
          </div>
          <Button 
            onClick={() => setShowNewTerminModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Termin
          </Button>
        </div>

        {/* Kalender */}
        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <Button onClick={previousMonth} variant="outline" size="sm">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <CardTitle className="text-2xl">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <Button onClick={nextMonth} variant="outline" size="sm">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Wochentage */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 text-sm py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Kalendertage */}
            <div className="grid grid-cols-7 gap-2">
              {/* Leere Zellen für Tage vor dem 1. des Monats */}
              {Array.from({ length: firstDay }).map((_, index) => (
                <div key={`empty-${index}`} className="h-24 bg-gray-50 rounded-lg"></div>
              ))}

              {/* Tage des Monats */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const dayTermine = getTermineForDay(day);
                const isToday = 
                  day === today.getDate() &&
                  currentDate.getMonth() === today.getMonth() &&
                  currentDate.getFullYear() === today.getFullYear();

                return (
                  <div
                    key={day}
                    className={`h-24 border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md ${
                      isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setSelectedDate(day)}
                  >
                    <div className={`text-sm font-semibold mb-1 ${
                      isToday ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {day}
                    </div>
                    <div className="space-y-1 overflow-y-auto max-h-16">
                      {dayTermine.map(termin => (
                        <div
                          key={termin.id}
                          className={`${termin.color} text-white text-xs px-2 py-1 rounded truncate`}
                          title={`${termin.uhrzeit} - ${termin.titel}`}
                        >
                          {termin.uhrzeit} {termin.titel}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Terminliste */}
        <Card>
          <CardHeader>
            <CardTitle>Anstehende Termine</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {termine
                .sort((a, b) => a.datum - b.datum)
                .map(termin => (
                  <div
                    key={termin.id}
                    className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className={`w-2 h-full ${termin.color} rounded`}></div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{termin.titel}</h3>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{termin.datum.toLocaleDateString('de-DE')}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{termin.uhrzeit}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <User className="w-4 h-4" />
                              <span>{termin.kunde}</span>
                            </div>
                            {termin.fahrzeug && (
                              <span className="text-gray-500">• {termin.fahrzeug}</span>
                            )}
                          </div>
                          {termin.ort && (
                            <div className="flex items-center space-x-1 mt-1 text-sm text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span>{termin.ort}</span>
                            </div>
                          )}
                        </div>
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          {termin.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Neuer Termin Modal */}
        {showNewTerminModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Neuer Termin</CardTitle>
                  <Button
                    onClick={() => setShowNewTerminModal(false)}
                    variant="ghost"
                    size="sm"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titel *
                    </label>
                    <Input
                      value={newTermin.titel}
                      onChange={(e) => setNewTermin({...newTermin, titel: e.target.value})}
                      placeholder="z.B. Fahrzeugbesichtigung"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Datum *
                      </label>
                      <Input
                        type="date"
                        value={newTermin.datum}
                        onChange={(e) => setNewTermin({...newTermin, datum: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Uhrzeit *
                      </label>
                      <Input
                        type="time"
                        value={newTermin.uhrzeit}
                        onChange={(e) => setNewTermin({...newTermin, uhrzeit: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kunde
                    </label>
                    <Input
                      value={newTermin.kunde}
                      onChange={(e) => setNewTermin({...newTermin, kunde: e.target.value})}
                      placeholder="Name des Kunden"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fahrzeug
                    </label>
                    <Input
                      value={newTermin.fahrzeug}
                      onChange={(e) => setNewTermin({...newTermin, fahrzeug: e.target.value})}
                      placeholder="z.B. BMW 320d"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ort
                    </label>
                    <Input
                      value={newTermin.ort}
                      onChange={(e) => setNewTermin({...newTermin, ort: e.target.value})}
                      placeholder="Adresse oder Ort"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notizen
                    </label>
                    <textarea
                      value={newTermin.notizen}
                      onChange={(e) => setNewTermin({...newTermin, notizen: e.target.value})}
                      placeholder="Zusätzliche Informationen..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <Button
                      onClick={() => setShowNewTerminModal(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleCreateTermin}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      Termin erstellen
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Termine;